import { Controller } from '@hotwired/stimulus'
import { PreviewClient } from 'api/preview_client'
import { FileAttacher, disablingSubmitButtonOnUpload } from 'api/file_attacher'
import { Confirmable, ConfirmableEvent, buildFormValueSnapshot } from 'mixins/confirmable'
import dragDrop from 'drag-drop'
import Routes from 'common/rails_routes'
import { RailsFetcher } from 'api/rails_fetcher'
import { Toast } from 'bootstrap'
import { showPublicDailyReportNotice } from 'common/modal'
import { MarkdownEditable } from 'mixins/markdown_editable'
import { Turbo } from '@hotwired/turbo-rails'
class DailyReportDrafts extends MarkdownEditable(Confirmable(Controller)) {
  connect () {
    this.initConfirmableForms([this.formTarget.id])
    this.client = new PreviewClient({ previewTarget: this.previewTarget, bodyTarget: this.bodyTarget })
    this.fileAttacher = new FileAttacher({
      bodyTarget: this.bodyTarget,
      callback: disablingSubmitButtonOnUpload(this.submitTarget)
    })
    dragDrop(this.bodyTarget, (files) => {
      this.fileAttacher.uploadFiles(files, window)
    })
    this.connectMarkdownEditable(this.bodyTarget, this.previewTarget)
  }

  disconnect () {
    this.disconnectMarkdownEditable()
    super.disconnect()
  }

  updatePreview () {
    this.client.call()
  }

  clickFileUploadButton (e) {
    e.preventDefault()
    this.filesTarget.click()
  }

  changeFiles () {
    const files = this.filesTarget.files
    if (files === null) {
      throw new Error('No file found')
    }
    this.fileAttacher.uploadFiles(Array.from(files), window)
  }

  pasteClipboardData (e) {
    if (e.clipboardData.types.indexOf('Files') > -1) {
      this.fileAttacher.uploadFiles(Array.from(e.clipboardData.files), window)
      e.preventDefault()
    }
  }

  scroll () {
    this.scrollMarkdownPreview()
  }

  open (e) {
    if (this.requireOpenConfirmation()) {
      e.preventDefault()
      this.showOpenConfirmation(e.currentTarget)
    }
  }

  save (e) {
    if (e.code === 'KeyS' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      this.submit()
    }
  }

  requireOpenConfirmation () {
    return this.openConfirmedTarget.value !== 'true'
  }

  showOpenConfirmation (submitter) {
    if (!this.formTarget.checkValidity()) {
      this.formTarget.reportValidity()
      return
    }
    const OnClickHandler = () => {
      this.formTarget.dispatchEvent(new Event(ConfirmableEvent.reset))
      this.formTarget.requestSubmit(submitter)
    }
    const OnHideHandler = () => { }
    // HACK: Confirmableのデータ同期をしないとsystem specで Blocked confirmが出る
    this.formTarget.dispatchEvent(new Event(ConfirmableEvent.reset))
    showPublicDailyReportNotice(OnClickHandler, OnHideHandler)
  }

  submit () {
    if (!this.formTarget.reportValidity()) return
    this.idValue === 0 ? this.create() : this.update()
  }

  create () {
    const savedValues = buildFormValueSnapshot(this.formTarget)
    RailsFetcher.call({
      url: Routes.daily_report_drafts_path({ format: 'json' }),
      method: 'POST',
      body: this.payload()
    }).then(response => {
      if (!response.ok) {
        return response.text().then(html => Turbo.renderStreamMessage(html))
      }
      response.json().then(json => {
        this.idValue = json.id
        this.errorsTarget.innerHTML = ''
        this.errorsTarget.id = `error${json.id}`
        // NOTE: 日報の新規作成は必ず下書きから行われるので遷移先は必ず下書きの編集画面
        this.formTarget.setAttribute('action', Routes.daily_report_draft_path(this.idValue))
        const input = document.createElement('input')
        input.setAttribute('type', 'hidden')
        input.setAttribute('name', '_method')
        input.setAttribute('value', 'patch')
        this.formTarget.appendChild(input)
        history.pushState(null, '', Routes.edit_daily_report_draft_path(this.idValue))
        this.formTarget.dispatchEvent(new CustomEvent(ConfirmableEvent.reset, { detail: { savedValues } }))
        this.showToast()
      })
    })
  }

  update () {
    this.formTarget.requestSubmit(this.autosaveTarget)
  }

  payload () {
    return { daily_report_draft: { title: this.titleTarget.value, body: this.bodyTarget.value, published_on: this.publishedAtTarget.value } }
  }

  showAutosaveToast (event) {
    const { success, formSubmission } = event.detail
    if (!success || formSubmission.submitter !== this.autosaveTarget) return
    this.showToast()
  }

  showToast () {
    const delay = process.env.NODE_ENV === 'test' ? 10000 : 5000
    const toast = new Toast(this.alertTarget, { delay })
    toast.show()
  }
}
DailyReportDrafts.targets = ['preview', 'title', 'body', 'files', 'submit', 'form', 'publishedAt', 'alert', 'openConfirmed', 'autosave', 'errors']
DailyReportDrafts.values = { id: Number, draft: Boolean }
export default DailyReportDrafts
